import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { CancelSubsciptionPlan, FetchSubsciptionPlans, PuchaseSubsciptionPlan } from "../../services/stripe.servic4e";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import moment from "moment";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PK
console.log("===  1231232123123 ==================", STRIPE_PK)
const stripePromise = loadStripe(STRIPE_PK);

const getPlanCurrencySymbol = (plan) => {
  if (plan?.currency_symbol) return plan.currency_symbol;
  if (plan?.currencySymbol) return plan.currencySymbol;
  const curr = plan?.currency ? plan.currency.toUpperCase() : "GBP";
  if (curr === "GBP") return "£";
  if (curr === "USD") return "$";
  return "£";
};

function CheckoutForm({ clientSecret, selectedPlan, onCancel, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setSubmitting(true);
    setCardError(null);

    try {
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (result.error) {
        setCardError(result.error.message);
        Swal.fire({
          icon: "error",
          title: "Payment Failed",
          text: result.error.message || "Payment failed. Please check your card and try again.",
          confirmButtonColor: "#f37a20",
          position: "center",
        });
        setSubmitting(false);
      } else if (
        result.paymentIntent?.status === "succeeded" ||
        result.paymentIntent?.status === "processing"
      ) {
        Swal.fire({
          icon: "success",
          title: "Payment Successful!",
          text: "Subscription purchased successfully!",
          confirmButtonColor: "#f37a20",
          position: "center",
        }).then(() => {
          onSuccess();
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "Payment Confirmed!",
          text: "Subscription payment confirmed!",
          confirmButtonColor: "#f37a20",
          position: "center",
        }).then(() => {
          onSuccess();
        });
      }
    } catch (err) {
      console.error("Payment confirmation error:", err);
      Swal.fire({
        icon: "error",
        title: "Payment Error",
        text: err.message || "An unexpected error occurred during payment.",
        confirmButtonColor: "#f37a20",
        position: "center",
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: "600px", margin: "20px auto" }}>
      <div className="card border-0 shadow-sm rounded-4 p-4 p-md-5 bg-white">
        <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
          <div>
            <h4 className="h4-headline mb-1">Complete Subscription</h4>
            <p className="text-muted small mb-0">
              {selectedPlan?.name ? `Subscribing to ${selectedPlan.name}` : "Enter your card details to complete payment"}
            </p>
          </div>
          {selectedPlan?.price ? (
            <span className="badge bg-warning-subtle text-dark fs-5 fw-bold px-3 py-2 rounded-pill">
              {getPlanCurrencySymbol(selectedPlan)}{selectedPlan.price}
            </span>
          ) : null}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="form-label subtitle-2 fw-semibold text-dark mb-2">
              Card Information <span className="text-danger">*</span>
            </label>
            <div
              className="p-3 rounded-3"
              style={{
                border: cardError ? "1px solid #dc3545" : "1px solid #ced4da",
                backgroundColor: "#ffffff",
              }}
            >
              <CardElement
                options={{
                  hidePostalCode: true,
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#212529",
                      fontFamily: "inherit",
                      "::placeholder": {
                        color: "#6c757d",
                      },
                    },
                    invalid: {
                      color: "#dc3545",
                      iconColor: "#dc3545",
                    },
                  },
                }}
                onChange={(e) => {
                  if (e.error) {
                    setCardError(e.error.message);
                  } else {
                    setCardError(null);
                  }
                }}
              />
            </div>
            {cardError && <small className="text-danger d-block mt-1">{cardError}</small>}
          </div>

          <div className="d-flex flex-column flex-sm-row gap-2 justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary px-4 py-2"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel / Back to Plans
            </button>
            <button
              type="submit"
              className="btn btn-oring px-4 py-2 d-flex align-items-center justify-content-center gap-2"
              disabled={!stripe || submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span>Processing Payment...</span>
                </>
              ) : (
                <span>Pay {selectedPlan?.price ? `${getPlanCurrencySymbol(selectedPlan)}${selectedPlan.price}` : "Now"}</span>
              )}
            </button>
          </div>

          <div className="text-center mt-4 pt-2 border-top">
            <small className="text-muted">
              🔒 Guaranteed 256-bit encrypted secure checkout with Stripe.
            </small>
          </div>
        </form>
      </div>
    </div>
  );
}

const formatDuration = (days, months, showDays = false) => {
  const numDays = Number(days);
  let durationStr = "";
  if (months && Number(months) > 0) {
    const numMonths = Number(months);
    if (Number.isInteger(numMonths)) {
      durationStr = `${numMonths} ${numMonths === 1 ? "Month" : "Months"}`;
    }
  }
  if (!durationStr) {
    if (numDays === 365) {
      durationStr = "12 Months (1 Year)";
    } else if (numDays && numDays % 30 === 0) {
      const m = numDays / 30;
      durationStr = `${m} ${m === 1 ? "Month" : "Months"}`;
    } else if (numDays && numDays >= 30) {
      const m = Math.round(numDays / 30);
      durationStr = `${m} ${m === 1 ? "Month" : "Months"}`;
    } else if (numDays) {
      durationStr = `${numDays} ${numDays === 1 ? "Day" : "Days"}`;
    }
  }

  if (showDays && numDays && durationStr && !durationStr.includes("Day") && !durationStr.includes("Year")) {
    return `${durationStr} (${numDays} Days)`;
  }
  return durationStr;
};

const sortSubscriptionPlans = (plans) => {
  if (!Array.isArray(plans)) return [];
  const getRank = (plan) => {
    const name = (plan?.name || "").toLowerCase().trim();
    if (name.includes("bronze") || name.includes("free") || name.includes("basic") || name.includes("starter")) return 1;
    if (name.includes("silver") || name.includes("standard") || name.includes("medium")) return 2;
    if (name.includes("gold") || name.includes("premium") || name.includes("pro")) return 3;
    if (name.includes("platinum") || name.includes("diamond") || name.includes("enterprise")) return 4;
    return 100 + (Number(plan?.price) || 0);
  };

  return [...plans].sort((a, b) => {
    const rankA = getRank(a);
    const rankB = getRank(b);
    if (rankA !== rankB) {
      return rankA - rankB;
    }
    return (Number(a?.price) || 0) - (Number(b?.price) || 0);
  });
};

export default function ManageSubscription() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [subscriptionsPlans, setSubscriptionsPlans] = useState([]);

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  const fetchSubscriptionPlans = () => {
    setClientSecret(null);
    setSelectedPlan(null);
    setLoadingPlanId(null);
    setLoading(true);
    FetchSubsciptionPlans()
      .then((response) => {
        setLoading(false);
        const rawList = response?.data?.list || [];
        const activeList = rawList.filter(
          (plan) => plan.isSubscribed || plan.status === 1 || plan.status === undefined || plan.status === true
        );
        setSubscriptionsPlans(sortSubscriptionPlans(activeList));
      })
      .catch((err) => {
        setLoading(false);
        setError(err.message || "Failed to fetch subscriptions");
      });
  };

  const cancelSubscriptionPlan = (userSubscriptionID) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_cancel_plan") || "Cancel plan?",
      text: t("Are you sure you want to cancel subscription?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("btn_yes") || "Yes, Cancel",
      cancelButtonText: t("btn_cancel") || "No, Keep Plan",
      position: "center",
      toast: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setLoadingPlanId(userSubscriptionID);
        CancelSubsciptionPlan(userSubscriptionID)
          .then(() => {
            setLoadingPlanId(null);
            fetchSubscriptionPlans();
          })
          .catch((err) => {
            setLoadingPlanId(null);
            setError(err.message || "Failed to cancel subscriptions");
          });
      }
    });
  };

  const purchaseSubsPlan = (priceId, paidPlan = true, planObj = null) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_purchase_plan") || "Purchase Plan?",
      text: t("Are you sure you want to buy subscription?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f37a20",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("btn_yes") || "Yes, Proceed",
      cancelButtonText: t("btn_cancel") || "Cancel",
      position: "center",
      toast: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setLoadingPlanId(planObj?._id || priceId);
        PuchaseSubsciptionPlan(priceId)
          .then((response) => {
            if (!response.success) throw response;
            if (paidPlan) {
              setSelectedPlan(planObj);
              setClientSecret(response.data.clientSecret);
            } else {
              Swal.fire({
                icon: "success",
                title: "Success!",
                text: "Subscription purchased successfully!",
                confirmButtonColor: "#f37a20",
                position: "center",
              });
              fetchSubscriptionPlans();
            }
            setLoadingPlanId(null);
          })
          .catch((err) => {
            setLoadingPlanId(null);
            Swal.fire({
              icon: "error",
              title: "Purchase Failed",
              text: err?.response?.data?.message || err?.message || "Failed to purchase plan",
              confirmButtonColor: "#f37a20",
              position: "center",
            });
          });
      }
    });
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="customer-part">
          <div className="card-box">
            <div className="notification notification-list-bg">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 20px",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                <div
                  className="spinner-border"
                  role="status"
                  style={{
                    width: "42px",
                    height: "42px",
                    color: "#F27438",
                    borderWidth: "3.5px",
                  }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p
                  style={{
                    marginTop: "16px",
                    marginBottom: 0,
                    fontSize: "15px",
                    fontWeight: "500",
                    color: "#6B7280",
                    textAlign: "center",
                  }}
                >
                  Loading subscriptions...
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content">
        <div className="customer-part">
          <div className="card-box">
            <div className="notification notification-list-bg">
              <div className="text-center py-4">
                <div className="alert alert-danger">
                  <p className="mb-2">Error: {error}</p>
                  <button className="btn btn-primary btn-sm" onClick={fetchSubscriptionPlans}>
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (subscriptionsPlans?.length === 0) {
    return (
      <div className="main-content">
        <div className="customer-part">
          <div className="card">
            <div className="notification-box">
              <figure>
                <img src="/favicon/notification-img.svg" alt="No plans" />
                <h2 className="h2-headline mt-4">No Plans Here</h2>
                <p className="text-gray">There are no subscriptions to show right now.</p>
              </figure>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="customer-part">
        <div className="card-box">
          <div className="d-flex justify-content-between align-items-center mb-4 w-100">
            <button
              type="button"
              className="back-link btn btn-link p-0 text-decoration-none"
              onClick={() => navigate(-1)}
            >
              <i className="fi fi-rr-arrow-small-left"></i>
              <span>Back</span>
            </button>
            <h2 className="h3-headline mb-0">Subscription Plans</h2>
            <div style={{ width: "60px" }}></div>
          </div>

          {clientSecret ? (
            <Elements stripe={stripePromise}>
              <CheckoutForm
                clientSecret={clientSecret}
                selectedPlan={selectedPlan}
                onCancel={() => {
                  setClientSecret(null);
                  setSelectedPlan(null);
                }}
                onSuccess={() => {
                  fetchSubscriptionPlans();
                }}
              />
            </Elements>
          ) : (
            <div style={{ boxShadow: "none" }} className="w-100">
              {(() => {
                const activeSubscriptionPlan = subscriptionsPlans.find(
                  (p) => p.isSubscribed && p.subscriptionData?.status === "active"
                );

                if (!activeSubscriptionPlan) return null;

                const isCancelled = activeSubscriptionPlan.subscriptionData?.cancelScheduled;

                return (
                  <div
                    className="p-3 p-md-4 mb-4 rounded-4 shadow-sm"
                    style={{
                      backgroundColor: "#FFF9F2",
                      border: "1.5px solid #F37A20",
                    }}
                  >
                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                          <span
                            className={`badge rounded-pill px-3 py-1 ${
                              isCancelled ? "bg-warning text-dark" : "bg-success text-white"
                            }`}
                            style={{
                              backgroundColor: isCancelled ? undefined : "#198754",
                              color: isCancelled ? "#212529" : "#FFFFFF",
                              fontWeight: "600",
                              fontSize: "12px",
                            }}
                          >
                            {isCancelled ? "Cancellation Scheduled" : "Active Subscription"}
                          </span>
                          <h4 className="mb-0 fw-bold text-dark">{activeSubscriptionPlan.name} Plan</h4>
                          <span className="badge bg-warning-subtle text-dark fw-bold px-2.5 py-1 rounded-pill">
                            {activeSubscriptionPlan.paidPlan ? `${getPlanCurrencySymbol(activeSubscriptionPlan)}${activeSubscriptionPlan.price}` : "Free"}
                          </span>
                        </div>
                        <p className="text-muted small mb-0" style={{ maxWidth: "550px" }}>
                          {activeSubscriptionPlan.description}
                        </p>
                      </div>

                      {activeSubscriptionPlan.paidPlan && (
                        <div className="d-flex flex-wrap gap-3 gap-md-4 align-items-center bg-white p-3 rounded-3 shadow-xs">
                          <div>
                            <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                              <i className="fi fi-rr-calendar me-1"></i>Start Date
                            </span>
                            <strong className="text-dark small">
                              {activeSubscriptionPlan.subscriptionData?.startDate
                                ? moment(activeSubscriptionPlan.subscriptionData.startDate).format("DD MMM YYYY")
                                : "N/A"}
                            </strong>
                          </div>

                          {activeSubscriptionPlan.subscriptionData?.endDate && (
                            <>
                              <div className="vr d-none d-sm-block my-1" style={{ opacity: 0.15 }}></div>
                              <div>
                                <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                                  <i className="fi fi-rr-calendar me-1"></i>End Date
                                </span>
                                <strong className="text-dark small">
                                  {moment(activeSubscriptionPlan.subscriptionData.endDate).format("DD MMM YYYY")}
                                </strong>
                              </div>
                            </>
                          )}

                          <div className="ms-auto ms-lg-2">
                            {isCancelled ? (
                              <span
                                className="badge px-3 py-2 rounded-3"
                                style={{
                                  backgroundColor: "#F3F4F6",
                                  color: "#6B7280",
                                  fontSize: "13px",
                                  fontWeight: "500",
                                }}
                              >
                                Expires {moment(activeSubscriptionPlan.subscriptionData?.endDate).format("DD MMM YYYY")}
                              </span>
                            ) : (
                              <button
                                type="button"
                                disabled={Boolean(loadingPlanId)}
                                className="btn btn-danger text-white px-3 py-2 rounded-3"
                                style={{
                                  height: "38px",
                                  width: "auto",
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  whiteSpace: "nowrap",
                                  backgroundColor: "#DC3545",
                                  borderColor: "#DC3545",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "8px 18px",
                                }}
                                onClick={() =>
                                  cancelSubscriptionPlan(activeSubscriptionPlan.subscriptionData?._id)
                                }
                              >
                                {loadingPlanId === activeSubscriptionPlan.subscriptionData?._id
                                  ? "Cancelling..."
                                  : "Cancel Plan"}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div className="row g-4 justify-content-center w-100 mx-auto">
                {subscriptionsPlans.map((plan) => {
                  const isActive = plan.isSubscribed && plan.subscriptionData?.status === "active";
                  const isCancelled = plan.subscriptionData?.cancelScheduled;
                  const anyActivePaid = subscriptionsPlans.some(
                    (p) => p.isSubscribed && p.paidPlan && p.subscriptionData?.status === "active"
                  );

                  return (
                    <div
                      className="col-12 col-md-6 col-lg-4 d-flex justify-content-center"
                      key={plan._id}
                    >
                      <div
                        className="p-4 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-between w-100"
                        style={{
                          backgroundColor: isActive ? "#FFF9F2" : "#f9f9f9",
                          border: isActive ? "2px solid #F37A20" : "1px solid #eeeeee",
                          minWidth: "280px",
                          maxWidth: "380px",
                        }}
                      >
                        <div>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h4 className="mb-0 fw-bold">{plan.name}</h4>
                            {isActive && !isCancelled && (
                              <span
                                className="badge bg-success text-white rounded-pill px-3 py-1"
                                style={{
                                  backgroundColor: "#198754",
                                  color: "#FFFFFF",
                                  fontWeight: "600",
                                  fontSize: "12px",
                                }}
                              >
                                Active
                              </span>
                            )}
                            {isActive && isCancelled && (
                              <span
                                className="badge bg-warning text-dark rounded-pill px-3 py-1"
                                style={{ fontWeight: "600", fontSize: "12px" }}
                              >
                                Cancelled
                              </span>
                            )}
                          </div>

                          <div className="d-flex align-items-baseline gap-1 mb-2">
                            <h3 className="fw-bold mb-0 text-dark">
                              {plan.paidPlan ? `${getPlanCurrencySymbol(plan)}${plan.price}` : "Free Plan"}
                            </h3>
                          </div>

                          {isActive && plan.paidPlan && plan.subscriptionData && (
                            <div
                              className="p-3 mb-3 rounded-3"
                              style={{
                                backgroundColor: "#FFF4EB",
                                border: "1px solid #FFE0C2",
                              }}
                            >
                              <div
                                className="d-flex justify-content-between align-items-center mb-2 pb-1"
                                style={{
                                  borderBottom: plan.subscriptionData.endDate
                                    ? "1px solid #FCE1CA"
                                    : "none",
                                }}
                              >
                                <span className="text-muted small fw-medium">Start Date:</span>
                                <span className="fw-bold text-dark small">
                                  {plan.subscriptionData.startDate
                                    ? moment(plan.subscriptionData.startDate).format("DD MMM YYYY")
                                    : "N/A"}
                                </span>
                              </div>
                              {plan.subscriptionData.endDate && (
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-muted small fw-medium">End Date:</span>
                                  <span className="fw-bold text-dark small">
                                    {moment(plan.subscriptionData.endDate).format("DD MMM YYYY")}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          <p className="text-muted mb-4">{plan.description}</p>
                        </div>
                        <div>
                          {isActive && plan.paidPlan ? (
                            <button
                              type="button"
                              disabled={Boolean(loadingPlanId) || isCancelled}
                              className="btn btn-danger w-100 py-2 rounded-3 text-white"
                              style={{
                                backgroundColor: isCancelled ? "#6c757d" : "#DC3545",
                                borderColor: isCancelled ? "#6c757d" : "#DC3545",
                                color: "#FFFFFF",
                                fontWeight: "600",
                              }}
                              onClick={() => cancelSubscriptionPlan(plan.subscriptionData?._id)}
                            >
                              {loadingPlanId === plan.subscriptionData?._id || loadingPlanId === plan._id
                                ? "Cancelling..."
                                : isCancelled
                                ? "Cancelled"
                                : "Cancel Plan"}
                            </button>
                          ) : !isActive && plan.paidPlan ? (
                            <button
                              type="button"
                              onClick={() => purchaseSubsPlan(plan.stripePlanID, plan.paidPlan, plan)}
                              disabled={Boolean(loadingPlanId) || anyActivePaid}
                              className="btn btn-oring w-100 py-2 rounded-3"
                            >
                              {loadingPlanId === plan._id || loadingPlanId === plan.stripePlanID
                                ? "Processing..."
                                : "Buy"}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
